<template>
  <v-dialog
    :value="show"
    persistent
    lazy
    width="1280"
    :fullscreen="$vuetify.breakpoint.xs"
    @input="(val) => $emit('update:show', val)"
  >
    <v-card class="v-dialog-card-msr-style">
      <v-card-title class="d-block">
        <v-layout align-center>
          <v-layout
            align-center
            wrap
          >
            <div class="headline mb-2">
              {{ $t('createSlideshow') }}
            </div>
          </v-layout>
          <v-btn
            class="ml-auto mr-0 my-0"
            flat
            icon
            :title="$t('close')"
            @click="close()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <v-layout
          :align-start="$vuetify.breakpoint.smAndUp"
          :align-center="$vuetify.breakpoint.xs"
          v-bind="layout"
        >
          <!-- Preview Player -->
          <v-layout
            column
            align-center
            class="mb-4"
          >
            <!--
            We add all images to the DOM and just hide/show as needed,
            which avoids the little delay and annoying flickering that
            swapping just the URL would result in.
            -->
            <div
              style="position: relative"
              :style="{
                'min-width': previewWidth + 'px',
                'min-height': previewHeight + 'px',
              }"
            >
              <!-- The crossfading is faked by overlaying the current
                and next image on top of each other and fading out the
                top layer for the last bit of time. -->
              <img
                v-for="(image, imageIndex) in sortableImages"
                v-show="
                  image == playerImage || playerImageIndex == imageIndex - 1
                "
                :key="image.uuid"
                :src="image.src"
                :width="previewWidth"
                :height="previewHeight"
                class="unselectable slide"
                :class="{
                  'fading-out': shouldImageFadeOut(imageIndex),
                }"
                style="position: absolute"
                :style="{
                  'z-index': sortableImages.length + 1 - imageIndex,
                  'object-fit': imageFitMode,
                  background: backgroundColor,
                }"
              >
            </div>
            <!-- Player Toolbar -->
            <v-layout
              justify-center
              align-center
              class="mt-3"
              style="position: relative"
            >
              <!-- Slide index -->
              <div
                class="subheading"
                style="position: absolute"
                :style="{
                  top: $vuetify.breakpoint.xs ? '12px' : '16px',
                  left: $vuetify.breakpoint.xs ? '-48px' : '-72px',
                }"
              >
                <span class="grey--text text--darken-1">{{
                  (playerImageIndex + 1) | formatSlidePosition(slidesNumDigits)
                }}</span>
                / {{ sortableImages.length }}
              </div>
              <!-- Play Control Buttons -->
              <v-btn
                class="elevation-0 ml-0 play-control-button"
                :class="{'play-control-button--mobile': $vuetify.breakpoint.xs}"
                @click="goToPreviousImage()"
              >
                <v-icon>skip_previous</v-icon>
              </v-btn>
              <v-btn
                class="elevation-0 mx-0 play-control-button"
                :class="{'play-control-button--mobile': $vuetify.breakpoint.xs}"
                :color="playerPlaying ? 'info' : 'initial'"
                :dark="playerPlaying"
                @click="togglePlayPause()"
              >
                <v-icon v-if="playerPlaying">
                  pause
                </v-icon>
                <v-icon v-else>
                  play_arrow
                </v-icon>
              </v-btn>
              <v-btn
                class="elevation-0 mr-0 play-control-button"
                :class="{'play-control-button--mobile': $vuetify.breakpoint.xs}"
                @click="goToNextImage()"
              >
                <v-icon>skip_next</v-icon>
              </v-btn>
              <!-- Current Time -->
              <div
                class="subheading"
                style="position: absolute"
                :style="{
                  top: $vuetify.breakpoint.xs ? '12px' : '16px',
                  right: $vuetify.breakpoint.xs ? '-112px' : '-138px',
                }"
              >
                <span class="grey--text text--darken-1">{{
                  playerTimeSeconds | formatSeconds
                }}</span>
                /
                {{ overallDurationSeconds | formatSeconds }}
              </div>
            </v-layout>
            <div
              v-if="playerImage"
              class="subheading grey--text text--darken-3 mt-3"
            >
              {{ playerImage.file.name }}
            </div>
          </v-layout>
          <!-- Options -->
          <v-layout
            column
            class="mb-2"
            :class="{'mr-5': $vuetify.breakpoint.smAndUp}"
            :style="{'max-width': $vuetify.breakpoint.xs ? 'initial' : '184px'}"
          >
            <!-- Target Format -->
            <v-select
              v-model="targetFormatModel"
              :items="availableTargetFormats"
              :label="$t('tvformat')"
            />
            <!-- Image Duration -->
            <v-layout align-center>
              <v-btn
                class="elevation-0 ml-0 pa-0"
                style="
                  min-width: 32px;
                  max-width: 32px;
                  min-height: 32px;
                  max-height: 32px;
                "
                :disabled="imageDuration <= imageDurationMin"
                data-cy="decrease-image-duration-btn"
                @click="imageDurationModel = Number(imageDuration) - 1"
              >
                <v-icon>remove</v-icon>
              </v-btn>
              <v-btn
                class="elevation-0 ml-0 mr-3 pa-0"
                style="
                  min-width: 32px;
                  max-width: 32px;
                  min-height: 32px;
                  max-height: 32px;
                "
                :disabled="imageDuration >= imageDurationMax"
                @click="imageDurationModel = Number(imageDuration) + 1"
              >
                <v-icon>add</v-icon>
              </v-btn>
              <v-text-field
                v-model="imageDurationModel"
                type="number"
                :min="imageDurationMin"
                :max="imageDurationMax"
                step="1"
                :label="$t('slideshowImageDuration')"
                :rules="[rules.secondsMin, rules.secondsMax]"
              />
            </v-layout>
            <!-- Image Fit Mode -->
            <v-select
              v-model="imageFitModeModel"
              :items="availableImageFitModes"
              :label="$t('slideshowImageFitMode')"
            />
            <!-- Background Color -->
            <v-slide-y-transition>
              <v-menu
                v-if="imageFitMode == 'contain'"
                v-model="showBackgroundColorMenu"
                lazy
                :close-on-content-click="false"
              >
                <div slot="activator">
                  <v-layout
                    align-center
                    class="background-color-activator py-2"
                  >
                    <div
                      class="outlined"
                      style="min-width: 24px; min-height: 24px"
                      :style="{background: backgroundColor}"
                    />
                    <span class="subheading ml-3">{{
                      $t('slideshowBackgroundColor')
                    }}</span>
                  </v-layout>
                </div>
                <v-card>
                  <v-card-text>
                    <color-picker
                      :color="backgroundColor"
                      :color-palette="colorPalette"
                      show-color-palette
                      ignore-alpha
                      @close="showBackgroundColorMenu = false"
                      @changed="(color) => (backgroundColor = color)"
                    />
                  </v-card-text>
                </v-card>
              </v-menu>
            </v-slide-y-transition>
          </v-layout>
        </v-layout>
        <v-layout
          align-end
          v-bind="layout"
        >
          <!-- Images -->
          <v-layout style="overflow: auto">
            <!-- Draggable Images -->
            <draggable
              v-model="sortableImages"
              v-bind="dragOptions"
              ghost-class="ghost"
              @change="onImageOrderChanged"
            >
              <span
                v-for="(image, imageIndex) in sortableImages"
                :key="image.uuid"
                :title="image.file.name"
                class="clickable"
                style="
                  display: inline-block;
                  position: relative;
                  padding: 4px;
                  border: 4px solid transparent;
                  border-radius: 12px;
                "
                :style="{
                  'max-width': imagePreviewConfig.width + 16 + 'px',
                  'max-height': imagePreviewConfig.height + 16 + 'px',
                  'border-color':
                    selectedImage == image
                      ? 'var(--color-info)'
                      : 'transparent',
                }"
                data-cy="draggable-images"
                @click="selectImageByIndex(imageIndex)"
              >
                <div
                  class="body-2 px-2 py-1"
                  style="
                    position: absolute;
                    color: white;
                    background: rgba(0, 0, 0, 0.66);
                    top: 4px;
                    left: 4px;
                    border-top-left-radius: 8px;
                  "
                >
                  {{ imageIndex + 1 }}
                </div>
                <v-btn
                  v-if="image == selectedImage"
                  small
                  class="ma-0 elevation-5"
                  color="error"
                  :title="$t('delete')"
                  :disabled="sortableImages.length <= 2"
                  style="
                    position: absolute;
                    max-width: 29px;
                    max-height: 29px;
                    min-width: 29px;
                    min-height: 29px;
                    top: 4px;
                    right: 4px;
                    border-top-right-radius: 8px;
                  "
                  @click.stop="removeImageByIndex(imageIndex)"
                >
                  <v-icon>close</v-icon>
                </v-btn>
                <img
                  :src="image.src"
                  :width="imagePreviewConfig.width"
                  :height="imagePreviewConfig.height"
                  class="unselectable elevation-3"
                  style="object-fit: cover; border-radius: 8px"
                >
              </span>
            </draggable>
          </v-layout>
          <div class="ml-4 mt-4">
            <!-- Disband edited Slideshow Button -->
            <v-btn
              v-if="editing"
              round
              dark
              large
              class="ml-0"
              @click="confirmDisband()"
            >
              {{ $t('disband') }}
            </v-btn>
            <!-- Confirmation Button -->
            <v-btn
              round
              dark
              large
              class="mr-0"
              color="msr"
              :disabled="!formIsValid"
              data-cy="confirm-slideshow-btn"
              @click="confirm()"
            >
              {{ $t('confirm') }}
            </v-btn>
          </div>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import draggable from 'vuedraggable';

import ColorPicker from '@/components/ColorPicker';

const FADE_OUT_DURATION_SECONDS = 0.5;

export default {
  components: {
    draggable,
    ColorPicker,
  },
  filters: {
    formatSlidePosition(position, digits) {
      return String(position).padStart(digits, '0');
    },
    formatSeconds(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${String(minutes).padStart(2, '0')}:${String(
        remainingSeconds
      ).padStart(2, '0')}`;
    },
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    /**
     * A list of objects like this:
     *
     * {
     *  uuid: <Uploadable.uuid: String)>,
     *  file: File|Blob,
     *  src: <createObjectURL: String>,
     * }
     */
    images: {
      type: Array[Object],
      default: () => [],
    },
    targetFormat: {
      type: String,
      default: 'landscape', // See below: availableTargetFormats
    },
    imageDuration: {
      type: Number,
      default: 7, // Seconds
    },
    imageFitMode: {
      type: String,
      default: 'contain', // See below: availableImageFitModes
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      imageDurationMin: 1,
      imageDurationMax: 60,

      colorPalette: ['#000000', '#ffffff'],
      showBackgroundColorMenu: false,
      backgroundColor: '#000000',

      playerPlaying: false,
      playerTimeSeconds: 0,
      playerTimeout: null,
      playerImageIndex: 0,

      selectedImage: null,
      sortableImages: [],
    };
  },
  computed: {
    slidePositionToFadeOutStartTime() {
      const positionToFadeOutStart = {};
      this.sortableImages.forEach((image, index) => {
        const position = index + 1;
        positionToFadeOutStart[position] =
          index * this.imageDuration +
          this.imageDuration -
          FADE_OUT_DURATION_SECONDS;
      });
      return positionToFadeOutStart;
    },
    targetFormatModel: {
      get() {
        return this.targetFormat;
      },
      set(value) {
        this.$emit('update:targetFormat', value);
      },
    },
    imageDurationModel: {
      get() {
        return this.imageDuration;
      },
      set(value) {
        this.$emit('update:imageDuration', value);
      },
    },
    imageFitModeModel: {
      get() {
        return this.imageFitMode;
      },
      set(value) {
        this.$emit('update:imageFitMode', value);
      },
    },
    layout() {
      if (this.$vuetify.breakpoint.xs) {
        return {column: true};
      }
      return {row: true};
    },
    slidesNumDigits() {
      return String(this.sortableImages.length).length;
    },
    previewModifier() {
      if (this.$vuetify.breakpoint.xs) {
        return 0.55;
      }
      return 1.33;
    },
    previewWidth() {
      return this.targetFormat === 'landscape'
        ? 576 * this.previewModifier
        : 216 * this.previewModifier;
    },
    previewHeight() {
      return this.targetFormat === 'landscape'
        ? 324 * this.previewModifier
        : 384 * this.previewModifier;
    },
    playerImage() {
      if (!this.sortableImages.length) {
        return null;
      }
      return this.sortableImages[this.playerImageIndex];
    },
    rules() {
      return {
        secondsMin: (v) =>
          (!!v && v >= this.imageDurationMin) || this.$t('tooShort'),
        secondsMax: (v) =>
          (!!v && v <= this.imageDurationMax) || this.$t('tooLong'),
      };
    },
    imagePreviewConfig() {
      return {
        width: 100,
        height: 100,
      };
    },
    dragOptions() {
      return {
        // animation: 150,  // Slows things down...
        disabled: false,
      };
    },
    availableTargetFormats() {
      return [
        {
          text: this.$t('landscapeShort') + ' (16:9)',
          value: 'landscape',
        },
        {
          text: this.$t('portraitShort') + ' (9:16)',
          value: 'portrait',
        },
      ];
    },
    availableImageFitModes() {
      return [
        {
          text: this.$t('slideshowImageFitModeContain'),
          value: 'contain',
        },
        {
          text: this.$t('slideshowImageFitModeCover'),
          value: 'cover',
        },
      ];
    },
    overallDurationSeconds() {
      return this.sortableImages.length * this.imageDuration;
    },
    formIsValid() {
      return (
        this.sortableImages.length >= 2 &&
        this.imageDuration >= this.imageDurationMin &&
        this.imageDuration <= this.imageDurationMax
      );
    },
    payload() {
      return {
        backgroundColor: this.backgroundColor || '#000000',
        imageDuration: this.imageDuration,
        imageFitMode: this.imageFitMode,
        images: [...this.sortableImages],
        targetFormat: this.targetFormat,
      };
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        this.sortableImages = [...this.images];
        this.selectImageByIndex(0);
      } else {
        this.sortableImages = [];
      }
    },
    playerPlaying: function () {
      this.simulatePlayer();
    },
    imageDuration: function () {
      this.recalculatePlayerTimeForCurrentImage();
    },
  },
  methods: {
    shouldImageFadeOut(imageIndex) {
      if (!this.playerPlaying) {
        return false;
      }
      const isLastImage = imageIndex == this.sortableImages.length - 1;
      const isCurrentImage = imageIndex == this.playerImageIndex;
      if (isLastImage || !isCurrentImage) {
        return false;
      }
      const slidePosition = imageIndex + 1;
      const fadeOutStart = this.slidePositionToFadeOutStartTime[slidePosition];
      return this.playerTimeSeconds >= fadeOutStart;
    },
    confirmDisband() {
      const confirmed = confirm(this.$t('confirmSlideshowDisband'));
      if (!confirmed) {
        return;
      }
      this.$emit('disband');
      this.close();
    },
    recalculatePlayerTimeForCurrentImage() {
      this.playerTimeSeconds = this.playerImageIndex * this.imageDuration;
    },
    /**
     * Move through slides over time while player is playing.
     *
     * Also implement a crossfading effect between slides.
     */
    simulatePlayer() {
      if (this.playerTimeout) {
        clearTimeout(this.playerTimeout);
      }

      const tickMilliseconds = 500; // Update every 0.5s.

      const vm = this;
      this.playerTimeout = setTimeout(() => {
        if (vm.playerPlaying) {
          vm.playerTimeSeconds += tickMilliseconds / 1000;

          let index = Math.floor(vm.playerTimeSeconds / vm.imageDuration);
          if (index > vm.sortableImages.length - 1) {
            index = 0;
            vm.playerTimeSeconds = 0;
          }

          if (this.playerImageIndex != index) {
            const updateTime = false;
            vm.selectImageByIndex(index, updateTime);
          }

          vm.simulatePlayer();
        }
      }, tickMilliseconds);
    },
    /**
     * Ensure selected image stays visible in the player while reordering.
     */
    onImageOrderChanged(event) {
      if (this.selectedImage) {
        this.playerImageIndex = this.sortableImages.indexOf(this.selectedImage);
      }
    },
    togglePlayPause() {
      this.playerPlaying = !this.playerPlaying;
    },
    goToNextImage() {
      const nextIndex = (this.playerImageIndex || 0) + 1;
      const index = nextIndex < this.sortableImages.length ? nextIndex : 0;
      this.selectImageByIndex(index);
    },
    goToPreviousImage() {
      const previousIndex = (this.playerImageIndex || 0) - 1;
      const index =
        previousIndex >= 0 ? previousIndex : this.sortableImages.length - 1;
      this.selectImageByIndex(index);
    },
    selectImageByIndex(index, updatePlayerTime = true) {
      const image = this.sortableImages[index];
      if (!image) {
        this.playerTimeSeconds = 0;
        return;
      }
      if (this.selectedImage === image) {
        this.selectedImage = null;
      } else {
        this.selectedImage = image;
        this.playerImageIndex = index;

        if (updatePlayerTime) {
          this.playerTimeSeconds = index * this.imageDuration;
        }
      }
    },
    removeImageByIndex(index) {
      if (this.sortableImages.length == 1) {
        return;
      }
      this.sortableImages.splice(index, 1);
      if (index == 0) {
        this.playerImageIndex = 0;
      } else {
        this.playerImageIndex -= 1;
      }
      this.selectImageByIndex(this.playerImageIndex);
    },
    close() {
      this.$emit('update:show', false);
    },
    confirm() {
      if (!this.formIsValid) {
        return;
      }
      this.$emit('confirm', this.payload);
      this.close();
    },
  },
};
</script>

<style scoped>
.ghost {
  opacity: 0.25;
}

.background-color-activator:hover {
  background: rgba(0, 0, 0, 0.08);
}

.play-control-button {
  min-width: 44px;
  max-width: 44px;
  min-height: 44px;
  max-height: 44px;
}
.play-control-button--mobile {
  min-width: 32px;
  max-width: 32px;
  min-height: 32px;
  max-height: 32px;
}

.slide {
  opacity: 1;
  /* Keep this timing in sync with FADE_OUT_DURATION_SECONDS above. */
  transition: opacity 0.5s linear;
}
.slide.fading-out {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/SlideshowDialog.vue